import React, { useState, useRef } from "react";
import SignaturePadModal from "./SignaturePadModal";
import "./SplitPagesComponent.style.css";
import { useHandleUserSignatures, usePdfPages } from "./hooks";
import { Rect } from "../../store/documents/DocumentReducer";
import { Loader } from "../../modules/loader";
import { useSplitPagesSelector } from "../../store/documents/DocumentSelector";

const SELECTION_AREA_MIN_WIDTH = 80;
const SELECTION_AREA_MIN_HEIGHT = 16;

export const SplitPagesComponent: React.FC = () => {
  const [selections, setSelections] = useState<{ [key: number]: Rect | null }>({});
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const { loading: pagesLoading } = usePdfPages();
  const pages = useSplitPagesSelector();

  const { signatures, addSignature } = useHandleUserSignatures();
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent, pageIndex: number) => {
    const rect = (e.currentTarget as HTMLImageElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    dragStart.current = { x, y };
    setIsSelecting(true);
    setSelections((prev) => ({ ...prev, [pageIndex]: null }));
    setSelectedPage(null);
  };

  const handleMouseMove = (e: React.MouseEvent, pageIndex: number) => {
    if (!isSelecting || !dragStart.current) return;

    const target = e.currentTarget as HTMLImageElement;
    const rect = target.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;

    x = Math.max(0, Math.min(x, rect.width));
    y = Math.max(0, Math.min(y, rect.height));

    const startX = dragStart.current.x;
    const startY = dragStart.current.y;

    const newSelection = {
      x: Math.min(x, startX),
      y: Math.min(y, startY),
      width: Math.abs(x - startX),
      height: Math.abs(y - startY),
    };

    setSelections((prev) => ({ ...prev, [pageIndex]: newSelection }));
  };

  const handleMouseUp = (e: React.MouseEvent, pageIndex: number) => {
    setIsSelecting(false);
    const selection = selections[pageIndex];
    if (selection && selection.width > 80 && selection.height > 16) {
      setSelectedPage(pageIndex);
    } else {
      setSelections((prev) => ({ ...prev, [pageIndex]: null }));
    }
  };

  const handleSignatureSave = (
    dataUrl: string,
    coords: { x: number; y: number },
    pageIndex: number
  ) => {
    const selection = selections[pageIndex];
    if (selection) {
      addSignature({ pageIndex, rect: selection, dataUrl });

      return;
    }
    setSelections((prev) => ({ ...prev, [pageIndex]: null }));
    setSelectedPage(null);
  };

  if (pagesLoading)
    return (
      <div className="d-flex justify-content-center loader-container">
        <Loader color="primary" size="l" />
      </div>
    );

  return (
    <>
      <div className="split-pages-wrapper">
        {pages?.map((src, i) => (
          <div key={i} style={{ position: "relative", marginBottom: 20 }}>
            <img
              src={src}
              alt={`Page ${i + 1}`}
              onMouseDown={(e) => handleMouseDown(e, i)}
              onMouseMove={(e) => handleMouseMove(e, i)}
              onMouseUp={(e) => handleMouseUp(e, i)}
              draggable={false}
            />

            {/* Selection Rectangle */}
            {selections[i] &&
              selectedPage === null &&
              (() => {
                const sel = selections[i]!;
                const isValid =
                  sel.width >= SELECTION_AREA_MIN_WIDTH && sel.height >= SELECTION_AREA_MIN_HEIGHT;
                return (
                  <div
                    style={{
                      position: "absolute",
                      left: sel.x,
                      top: sel.y,
                      width: sel.width,
                      height: sel.height,
                      border: `1px ${isValid ? "dashed green" : "dashed red"}`,
                      backgroundColor: isValid ? "rgba(0, 128, 0, 0.1)" : "rgba(255, 0, 0, 0.15)",
                      pointerEvents: "none",
                    }}
                  />
                );
              })()}

            {/* Render Saved Signatures */}
            {signatures
              .filter((s) => s.pageIndex === i)
              .map((sig, idx) => (
                <img
                  key={idx}
                  src={sig.dataUrl}
                  alt="signature"
                  style={{
                    position: "absolute",
                    top: sig.rect.y,
                    left: sig.rect.x,
                    width: sig.rect.width,
                    height: sig.rect.height,
                    pointerEvents: "none",
                  }}
                />
              ))}
          </div>
        ))}
      </div>

      {selectedPage !== null && selections[selectedPage] && (
        <SignaturePadModal
          pageIndex={selectedPage}
          coords={{ x: selections[selectedPage]!.x, y: selections[selectedPage]!.y }}
          width={selections[selectedPage]!.width}
          height={selections[selectedPage]!.height}
          isOpen={true}
          onClose={() => {
            setSelections((prev) => ({ ...prev, [selectedPage]: null }));
            setSelectedPage(null);
          }}
          onSave={handleSignatureSave}
        />
      )}
    </>
  );
};
